import React, { useState, useEffect } from "react";
import accessURL from "../../../components/utils/superAccess";
import DataTable from "react-data-table-component";
import { Company, CompTablesProps, Pagination } from "./types";
import { customStyles } from "../../../components/utils/utils";
import CompanyDetailsModal from "./CompanyDetailsModal";
import UpdatePaymentDetails from "../payments/UpdatePaymentDetails";
import Expire from "../payments/expire/Expire";
import DeleteCompany from "./delete/DeleteCompany";
import { Link } from "react-router-dom";
import { Users } from "lucide-react";

interface ApiResponse {
  companies: Company[];
  pagination: Pagination;
}

const CompTables: React.FC<CompTablesProps> = ({
  startDate,
  endDate,
  selectedYear,
}) => {
  // State management
  const [data, setData] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [companyName, setCompanyName] = useState("");

  // Modal state
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Filters state with all relevant fields
  const [filters, setFilters] = useState({
    startDate,
    endDate,
    selectedYear,
    search: "",
  });

  // Function to handle "View More" button click
  const handleViewMore = (company: Company) => {
    setSelectedCompany(company);
    setIsModalOpen(true);
  };

  // Function to close modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCompany(null);
  };

  // Define columns for the DataTable - only the first 4 columns
  const columns = [
    {
      name: "Company Name",
      selector: (row: Company) => row.name,
      sortable: true,
      width: "350px",
      cell: (row: Company) => (
        <div className="flex items-center">
          {row.file_path ? (
            <img
              src={row.file_path}
              alt={`${row.name} logo`}
              className="w-8 h-8 mr-2 rounded-full"
            />
          ) : (
            <div className="w-8 h-8 mr-2 bg-gray-200 rounded-full flex items-center justify-center">
              {row.name.charAt(0).toUpperCase()}
            </div>
          )}
          <span className="font-medium">{row.name}</span>
        </div>
      ),
    },
    {
      name: "companyID",
      selector: (row: Company) => row.customID,
      sortable: true,
      width: "250px",
    },
    {
      name: "Email",
      selector: (row: Company) => row.email,
      sortable: true,
      width: "250px",
    },
    {
      name: "Paid Status",
      selector: (row: Company) => row.paid_status,
      sortable: true,
      width: "250px",
      cell: (row: Company) => (
        <div
          className={`inline-block px-3 py-1 rounded-full font-medium text-sm ${
            row.paid_status === "trial"
              ? "bg-orange-100 text-orange-700"
              : row.paid_status === "paid"
              ? "bg-green-100 text-green-700"
              : "bg-red-100 text-red-700"
          }`}
        >
          {row.paid_status === "expired" ? "suspended" : row.paid_status}
        </div>
      ),
    },
    {
      name: "Call Rate",
      selector: (row: Company) => {
        const rate = Number(row.call_rate);
        if (isNaN(rate)) {
          return "0%";
        } else if (rate === 0) {
          return "0";
        } else {
          return `${rate.toFixed(2)}%`;
        }
      },
      width: "120px",
    },
    {
      name: "Total Customers",
      selector: (row: Company) =>
        row.customers?.customerAnalytics?.total_customers || 0,
      sortable: true,
      width: "200px",
    },
    {
      name: "Customers",
      cell: (row: Company) => (
        <Link
          to={`/administrator/customers/${row.customID}`}
          className="flex items-center space-x-1 hover:text-blue-500"
        >
          <Users size={16} className="text-gray-600" />
        </Link>
      ),
      width: "200px",
    },
    {
      name: "Actions",
      cell: (row: Company) => (
        <div className="flex space-x-2 items-center">
          <button
            onClick={() => handleViewMore(row)}
            className="px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-700 text-sm"
          >
            View More
          </button>
          <UpdatePaymentDetails id={row.id} />
          <Expire id={row.id} />
          {/* <DeleteCompany id={row.id} customID={row.customID} /> */}
        </div>
      ),
      sortable: false,
      width: "550px",
    },
  ];

  // Update filters when props change
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      startDate,
      endDate,
      selectedYear,
    }));
  }, [startDate, endDate, selectedYear]);

  // Function to fetch companies
  const fetchCompanies = async () => {
    setLoading(true);

    // Build query parameters
    const params: Record<string, string> = {
      page: currentPage.toString(),
      limit: perPage.toString(),
    };

    // Add filters to params
    if (filters.startDate) params.start_date = filters.startDate;
    if (filters.endDate) params.end_date = filters.endDate;
    if (filters.selectedYear) params.year = filters.selectedYear.toString();
    if (filters.search) params.name = filters.search;

    const queryParams = new URLSearchParams(params);
    const url = `super-admin/companies?${queryParams.toString()}`;

    try {
      const response = await accessURL.get<ApiResponse>(url);

      // Handle the data based on the actual response structure
      if (response.data && response.data.companies) {
        setData(response.data.companies);
        if (response.data.pagination) {
          setTotalRows(response.data.pagination.total_records);
        }
      } else if (response.data && response.data && response.data.companies) {
        // Fallback in case the structure is nested differently
        setData(response.data.companies);
        if (response.data.pagination) {
          setTotalRows(response.data.pagination.total_records);
        }
      } else {
        console.error("Unexpected response structure:", response.data);
        setData([]);
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchCompanies();
    }, 300); // 300ms delay for debouncing

    return () => clearTimeout(timer);
  }, [filters, currentPage, perPage]); // Dependencies that trigger refetch

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle rows per page change
  const handlePerRowsChange = (newPerPage: number) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
  };

  // Handle company name input change
  const handleCompanyNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value;
    setCompanyName(searchValue);
    setFilters((prev) => ({
      ...prev,
      search: searchValue,
    }));
    setCurrentPage(1); // Reset to first page on search
  };

  return (
    <div className="w-full">
      {/* Company name search input */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Enter company name"
          value={companyName}
          onChange={handleCompanyNameChange}
          className="p-2 border rounded w-64"
        />
      </div>

      <DataTable
        columns={columns}
        data={data}
        progressPending={loading}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        onChangeRowsPerPage={handlePerRowsChange}
        onChangePage={handlePageChange}
        sortServer
        paginationPerPage={perPage}
        striped
        highlightOnHover
        responsive
        persistTableHead
        customStyles={customStyles}
        noDataComponent={
          <div className="p-4 text-center text-gray-500">
            No Companies found
          </div>
        }
      />

      {/* Modal Component */}
      {isModalOpen && selectedCompany && (
        <CompanyDetailsModal
          company={selectedCompany}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default CompTables;
